import Application from "../../Application";
import FormDetailsPresenter, {
  IFormDetailsPresenter
} from "../../presenter/form/FormDetailsPresenter";
import { IFormService } from "../../service/form/FormService";
import { ICredentialsService } from "../../service/authentication/CredentialsService";
import { IReportingPeriod } from "../../model/reporting/ReportingPeriod";
import {
  ReportingPeriodPermissionAction,
  FormTypesStatuses
} from "../../common/Constants";
import { IReportingPeriodFormType } from "../../model/reporting/ReportingPeriodFormType";
import { INotificationService } from "../../service/notification/NotificationService";
import { IFormVersion } from "../../model/form/FormVersion";
import { IFormTypeItem } from "../../model/report/FormTypeItem";

export default class ShowFormDetailsInteractor {
  private application: Application;
  private output?: IFormDetailsPresenter;
  private formService: IFormService;
  private credentialsService: ICredentialsService;
  private notificationService: INotificationService;

  constructor(
    application,
    formService,
    credentialsService,
    notificationService
  ) {
    this.application = application;
    this.formService = formService;
    this.credentialsService = credentialsService;
    this.notificationService = notificationService;
  }

  async getReportingPeriods(applicantId: number, isPreviousForm?: boolean) {
    return isPreviousForm
      ? this.formService.getPreviousReportingPeriodsByApplicantId(applicantId)
      : this.formService.getReportingPeriodsByApplicantId(applicantId);
  }

  async getReportingPeriodId(
    applicantId: number,
    reportingPeriodApplicantId: number,
    isPreviousForm?: boolean
  ) {
    const reportingPeriods = await this.getReportingPeriods(
      applicantId,
      isPreviousForm
    );

    return reportingPeriods.find(
      (reportingPeriod: IReportingPeriod) =>
        reportingPeriod.id === reportingPeriodApplicantId
    )?.reportingPeriodId;
  }

  async checkReadRight(reportingPeriodApplicantId: number) {
    const hasRight = await this.formService.checkRightOfAccessToData(
      reportingPeriodApplicantId,
      ReportingPeriodPermissionAction.READ
    );
    return hasRight;
  }

  async getReportingPeriodFormType(
    reportingPeriodApplicantId: number,
    reportingPeriodFormTypeId: number
  ) {
    const reportingPeriodFormTypes =
      await this.formService.getReportingPeriodFormTypes(
        reportingPeriodApplicantId
      );
    const reportingPeriodFormType = reportingPeriodFormTypes.find(
      (reportingPeriodFormType) =>
        reportingPeriodFormType.id === reportingPeriodFormTypeId
    );
    return reportingPeriodFormType;
  }

  async checkEditRight(
    reportingPeriodApplicantId: number,
    reportingPeriodFormType: IReportingPeriodFormType
  ) {
    if (reportingPeriodFormType.formTypeStatus === FormTypesStatuses.OBSOLETE) {
      return false;
    }
    const hasRight = await this.formService.checkRightOfAccessToData(
      reportingPeriodApplicantId,
      ReportingPeriodPermissionAction.EDIT
    );
    return hasRight;
  }

  async getFormVersions(formReportingPeriodId: number) {
    const formVersions = await this.formService.getFormVersions(
      formReportingPeriodId
    );
    return formVersions;
  }

  async getInitialData(
    reportingPeriodApplicantId: number,
    reportingPeriodFormTypeId: number,
    applicantId: number,
    isPreviousForm?: boolean
  ) {
    try {
      const hasReadRight = await this.checkReadRight(
        reportingPeriodApplicantId
      );

      let reportingPeriodFormType: IReportingPeriodFormType | undefined;
      let hasEditRight: boolean | undefined;
      let formVersions: IFormVersion[] | undefined;
      let reportingPeriodId: number | undefined;
      let allowedFileTypes: string | undefined;
      let isSignedReportRequired: boolean = true;

      if (hasReadRight) {
        reportingPeriodFormType = await this.getReportingPeriodFormType(
          reportingPeriodApplicantId,
          reportingPeriodFormTypeId
        );

        hasEditRight = await this.checkEditRight(
          reportingPeriodApplicantId,
          reportingPeriodFormType || ({} as IReportingPeriodFormType)
        );

        formVersions = reportingPeriodFormType?.formReportingPeriodId
          ? await this.getFormVersions(
              reportingPeriodFormType.formReportingPeriodId
            )
          : ([] as IFormVersion[]);

        reportingPeriodId = await this.getReportingPeriodId(
          applicantId,
          reportingPeriodApplicantId,
          isPreviousForm
        );

        allowedFileTypes = await this.formService.getAllowedFileTypes();
        isSignedReportRequired =
          await this.formService.isSignedReportRequired();
      }
      return {
        reportingPeriodFormType,
        hasEditRight,
        formVersions,
        reportingPeriodId,
        allowedFileTypes,
        isSignedReportRequired
      };
    } catch (error) {
      this.notificationService.showError((error as any).message);
    }
  }

  execute(
    reportingPeriodApplicantId: number,
    reportingPeriodFormTypeId: number,
    versionId: number,
    isPreviousForm?: boolean
  ) {
    try {
      const applicantId = this.credentialsService.getApplicantId();
      this.output = FormDetailsPresenter({
        application: this.application,
        initialState: {
          formVersionList: [],
          isEditableForm: false,
          reportingPeriodApplicantId,
          isPreviousForm,
          applicantId,
          validationErrors: [],
          comparisonVersionsModalVisible: false,
          firstComparisonFormVersion: [],
          secondComparisonFormVersion: [],
          comparisonFormVersionsVisible: false,
          pdfReportVisbile: false,
          reportingPeriodFormTypeId,
          allowedFileTypes: "",
          isSignedReportRequired: true,
          signedReportValidationError: false
        }
      });

      this.getInitialData(
        reportingPeriodApplicantId,
        reportingPeriodFormTypeId,
        applicantId,
        isPreviousForm
      ).then((result) => {
        if (this.output && result) {
          this.output.setReportingPeriodFormType(
            result.reportingPeriodFormType
          );
          this.output.setRightOfEntry(result.hasEditRight || false);
          this.output.loadFormVersionList(
            result.formVersions || ([] as IFormVersion[]),
            versionId
          );
          this.output.loadReportingPeriodId(result.reportingPeriodId || 0);
          this.output.loadFormType({
            name: result.reportingPeriodFormType?.formName || ""
          } as IFormTypeItem);
          this.output.loadAllowedFileTypes(result.allowedFileTypes);
          this.output.loadIsSignedReportRequired(result.isSignedReportRequired);
        }
      });
    } catch (error) {
      return error;
    }

    return this.output;
  }
}
